import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import { Site } from "@/models/site"
import { useQueryKey } from "@/services/utils/useQueryKey"
import { HttpError, toHttpError } from "@/utils/api"
import { resolveQueryThrowOnError } from "@/utils/queryClient"
import { useQuery } from "@tanstack/react-query"
import type { UseQueryOptions } from "@tanstack/react-query"
import type { RecordProxy } from "spraypaint/lib-esm/proxies"

import { useRenameQueryResult } from "../utils"

export const isUnavailableSiteError = (error: unknown): boolean =>
  error instanceof HttpError && (error.status === 403 || error.status === 404)

interface GetSiteOptions {
  organizationId?: Organization["id"]
  siteId: string
}

/**
 * Gets site by id
 */
const getSite = async ({
  organizationId,
  siteId,
}: GetSiteOptions): Promise<Site | undefined> => {
  try {
    let query = Site.includes([
      "address",
      "location",
      "organization",
      "physical_details",
    ]).selectExtra([
      "building_type",
      "department_name",
      "number_of_floors",
      "sq_ft",
    ])

    if (organizationId) {
      query = query.where({ organization_id: organizationId })
    }

    const response: RecordProxy<Site> = await query.find(siteId)

    return response.data
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

interface UseGetSiteOptions {
  organizationId?: Organization["id"]
  queryOptions?: Omit<
    UseQueryOptions<Site | undefined, HttpError>,
    "queryFn" | "queryKey"
  >
}

/**
 * Returns a site by id
 *
 * @example
 * const {
 *   siteData,
 *   siteIsPending,
 *   siteError,
 *   siteIsError
 *  } = useGetSite("1", options)
 */
export const useGetSite = (
  siteId: string | undefined,
  options: UseGetSiteOptions = {}
) => {
  const { organizationId, queryOptions = {} } = options

  const queryKey = useQueryKey(ApiQueryName.Site, "getOne", siteId, {
    organizationId,
  })

  const {
    enabled,
    retry = (failureCount, error) => {
      if (isUnavailableSiteError(error)) {
        return false
      }

      return failureCount < 3
    },
    throwOnError = resolveQueryThrowOnError(
      (error) => !isUnavailableSiteError(error)
    ),
    ...restQueryOptions
  } = queryOptions

  const queryResult = useQuery<Site | undefined, HttpError>({
    ...restQueryOptions,
    enabled: Boolean(siteId) && (enabled ?? true),
    queryFn: () => {
      if (!siteId) {
        throw new Error("siteId is required to fetch site details")
      }

      return getSite({ organizationId, siteId })
    },
    queryKey,
    retry,
    throwOnError,
  })

  return useRenameQueryResult(queryResult, ApiQueryName.Site)
}
